import React, { useRef, useMemo, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { useControls, button } from "leva";
import { fragmentShader, vertexShader } from "./Shader/shaders";

const lerp = (start, end, t) => start * (1 - t) + end * t;

const Bubble = () => {
  const materialRef = useRef();

  const presetStates = {
    processing: {
      innerRadius: 0.27,
      outerRadius: 0.34,
      sheetThickness: 0.06,
      noisiness: 7,
      timeScale: 2,
      overallScale: 2,
    },
    standBy: {
      innerRadius: 0.25,
      outerRadius: 0.41,
      sheetThickness: 0.03,
      noisiness: 6.5,
      timeScale: 5,
      overallScale: 2,
    },
    listening: {
      innerRadius: 0.25,
      outerRadius: 0.35,
      sheetThickness: 0.01,
      noisiness: 7,
      timeScale: 5,
      overallScale: 1.5,
    },
    idle: {
      innerRadius: 0.25,
      outerRadius: 0.28,
      sheetThickness: 0.025,
      noisiness: 9,
      timeScale: 5,
      overallScale: 1,
    },
  };

  const [
    {
      innerRadius,
      outerRadius,
      sheetThickness,
      noisiness,
      overallScale,
      timeScale,
    },
    set,
  ] = useControls(() => ({
    innerRadius: { value: 0.25, min: 0, max: 1, step: 0.01 },
    outerRadius: { value: 0.38, min: 0, max: 1, step: 0.01 },
    sheetThickness: { value: 0.02, min: 0, max: 0.08, step: 0.001 },
    noisiness: { value: 6, min: 0, max: 10, step: 0.1 },
    timeScale: { value: 5, min: 0, max: 10, step: 0.1 },
    overallScale: { value: 1, min: 0, max: 5, step: 0.1 },
    idle: button(() => applyPreset("idle")),
    standBy: button(() => applyPreset("standBy")),
    listening: button(() => applyPreset("listening")),
    processing: button(() => applyPreset("processing")),
  }));

  const [currentState, setCurrentState] = useState("idle");
  const [yPos, setYPos] = useState(0)
  const [scaleFactor, setScaleFactor] = useState(1)
  const applyPreset = (preset) => {
    set(presetStates[preset]);
    setCurrentState(preset);
  };

  const { viewport } = useThree();
  useFrame(({ clock }) => {
    THREE.ColorManagement.legacyMode = true;
    const f = .1
    if (materialRef?.current?.uniforms) {
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
      materialRef.current.uniforms.INNER_RADIUS.value =
        lerp(materialRef.current.uniforms.INNER_RADIUS.value, innerRadius, f);
      materialRef.current.uniforms.OUTER_RADIUS.value =
        lerp(materialRef.current.uniforms.OUTER_RADIUS.value, outerRadius, f);

      materialRef.current.uniforms.SHEET_THICKNESS.value = lerp(materialRef.current.uniforms.SHEET_THICKNESS.value, sheetThickness, f);
      materialRef.current.uniforms.NOISINESS.value = lerp(materialRef.current.uniforms.NOISINESS.value, noisiness, f);
      materialRef.current.uniforms.TIME_SCALE.value = timeScale;

      setScaleFactor(lerp(scaleFactor, overallScale, 0.1))
      if (currentState === "listening") {
        setYPos(lerp(yPos, Math.sin(timeScale * clock.elapsedTime) / 3, .1))
      } else {
        setYPos(lerp(yPos, 0, .1))
      }
    }
  });

  const sMat = useMemo(
    () => (
      <shaderMaterial
        toneMapped={true}
        ref={materialRef}
        transparent
        opacity={2}
        side={2}
        uniforms={{
          uTime: { value: 0 },
          INNER_RADIUS: { value: 0.25 },
          OUTER_RADIUS: { value: 0.37 },
          SHEET_THICKNESS: { value: 0.02 },
          NOISINESS: { value: 6 },
          TIME_SCALE: { value: 5 },
        }}
        fragmentShader={fragmentShader}
        vertexShader={vertexShader}
      />
    ),
    []
  );
  const size = Math.min(viewport.width, viewport.height) * scaleFactor

  return (
    <mesh
      scale={[size, size, 1]}
      position={[0, yPos, 0]}
    >
      <planeGeometry args={[1, 1]} />
      {sMat}
    </mesh>
  );
};

const App = () => {
  return (
    <div className="myCanvas">
      <Canvas
        // className="myCanvas"
        style={{
          width: "100%",
          height: "100dvh",
          filter: "saturate(2.5)",
          background: "white",
        }}
      >
        <Bubble />
      </Canvas>
    </div>
  );
};

export default App;
